import { useState, useEffect } from "react";
import { Button } from "react-bootstrap";
import {
  MdEdit,
  MdAdd,
  MdWorkOutline,
  MdCalendarToday,
  MdTranslate,
  MdMedicalServices,
  MdSchool,
  MdVideoLibrary,
  MdCollections,
  MdMenuBook,
  MdArticle,
  MdWorkspacePremium,
  MdPayments,
  MdBadge,
  MdPhone,
  MdEmail,
  MdLocationOn,
  MdPictureAsPdf,
  MdDownload,
  MdOpenInNew,
  MdPlayArrow,
  MdVideocam,
} from "react-icons/md";
import {
  FaGlobe,
  FaLinkedinIn,
  FaTwitter,
  FaInstagram,
  FaFacebookF,
  FaYoutube,
} from "react-icons/fa";
import { AwardCard } from "../../../../Components/ui/AwardCard/AwardCard";
import { mapAwardToCardProps } from "../../../../api/awardsApi";
import { ArticleCard } from "../../../../Components/ui/ArticleCard/ArticleCard";
import { VideoCard } from "../../../../Components/ui/VideoCard/VideoCard";
import { UPLOADS_BASE } from "../../constants/api";
import { mapDoctorApiToView } from "../../utils/doctorFormHelpers";
import { mapArticleToCardProps } from "../../utils/newsArticleHelpers";
import { mapCmeToVideoCardProps } from "../../utils/digitalCmeHelpers";
import {
  getYoutubeThumbnailUrl,
  getYoutubeWatchUrl,
  resolveIntroductionVideo,
} from "../../utils/youtubeHelpers";

const DM_DEMO_DOCTOR = {
  id: "demo",
  prefix: "Senior Consultant — Cardiology",
  name: "Dr. Sanjay J",
  specialty: "Interventional Cardiologist",
  subtitle: "Apollo Hospitals, Chennai · MBBS, MD, DM (Cardiology)",
  experience: "12 yrs experience",
  consultFee: "₹1,500",
  language: "English, Tamil, Hindi",
  about:
    "Dr. Sanjay J is a senior interventional cardiologist with over 12 years of experience in diagnosing and treating complex heart conditions.",
  qualifications: [
    "MBBS — Madras Medical College",
    "MD (General Medicine) — Stanley Medical College",
    "DM (Cardiology) — AIIMS, New Delhi",
  ],
  introductionVideo: "https://www.w3schools.com/html/mov_bbb.mp4",
  profilePicPath: "",
  publications:
    "Outcome of primary PCI in acute STEMI patients — Indian Heart Journal, 2022.",
  fees: { consultation: "₹1,500", followUp: "₹800" },
  registration: { council: "Tamil Nadu Medical Council", number: "TNMC-78452" },
  contact: {
    phone: "+91 75503 66612",
    email: "dr.sanjay.j@apollohealth.in",
    address: "5/619, Krishna Nagar, Chennai — 600041",
  },
  connect: [
    { key: "website", label: "Website", icon: FaGlobe, url: "https://www.apollohospitals.com" },
    { key: "linkedin", label: "LinkedIn", icon: FaLinkedinIn, url: "https://www.linkedin.com" },
    { key: "twitter", label: "Twitter", icon: FaTwitter, url: "https://www.twitter.com" },
    { key: "instagram", label: "Instagram", icon: FaInstagram, url: "https://www.instagram.com" },
    { key: "facebook", label: "Facebook", icon: FaFacebookF, url: "https://www.facebook.com" },
  ],
  profilePdfPath: "",
};

const SOCIAL_CONFIG = [
  { key: "website", label: "Website", icon: FaGlobe, field: "website_address" },
  { key: "facebook", label: "Facebook", icon: FaFacebookF, field: "facebook_page" },
  { key: "twitter", label: "Twitter", icon: FaTwitter, field: "x_profile" },
  { key: "instagram", label: "Instagram", icon: FaInstagram, field: "instagram" },
  { key: "linkedin", label: "LinkedIn", icon: FaLinkedinIn, field: "linkedin" },
  { key: "youtube", label: "YouTube", icon: FaYoutube, field: "youtube_channel" },
];

const DmCard = ({ title, icon: Icon, children, className = "" }) => (
  <div className={`dm-card ${className}`}>
    {title && (
      <h3 className="dm-card-title">
        {Icon && <Icon />}
        {title}
      </h3>
    )}
    {children}
  </div>
);

const DmInfoRow = ({ label, value }) => (
  <div className="dm-info-row">
    <span className="dm-info-label">{label}</span>
    <span className="dm-info-value">{value}</span>
  </div>
);

const buildConnectLinks = (social = {}) =>
  SOCIAL_CONFIG
    .map(({ key, label, icon, field }) => ({
      key,
      label,
      icon,
      url: social[field] || "",
    }))
    .filter((l) => l.url && !String(l.url).includes("/embed/"));

const DmIntroductionVideo = ({ videoUrl }) => {
  const [playing, setPlaying] = useState(false);
  const resolved = resolveIntroductionVideo(videoUrl, UPLOADS_BASE);

  useEffect(() => {
    setPlaying(false);
  }, [videoUrl]);

  if (!resolved) {
    return <p className="dm-text dm-text--muted">No introduction video added yet.</p>;
  }

  if (resolved.kind === "file") {
    return (
      <div className="hm-video-wrap dm-intro-video">
        <video
          controls
          className="dm-intro-video__file"
          src={resolved.src}
          preload="metadata"
          playsInline
        >
          <track kind="captions" />
        </video>
      </div>
    );
  }

  if (playing) {
    return (
      <div className="hm-video-wrap dm-intro-video">
        <iframe
          title="Introduction video"
          src={`${resolved.embedUrl}?autoplay=1`}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  if (resolved.thumbnailUrl) {
    return (
      <div className="hm-video-thumb-wrap">
        <button
          type="button"
          className="hm-video-thumb"
          onClick={() => setPlaying(true)}
          aria-label="Play introduction video"
        >
          <img src={resolved.thumbnailUrl} alt="Introduction video preview" />
          <span className="hm-video-thumb-play">
            <MdPlayArrow size={48} />
          </span>
        </button>
        {resolved.watchUrl && (
          <a
            href={resolved.watchUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="hm-video-thumb-external"
            aria-label="Open video in new tab"
          >
            <MdOpenInNew size={18} />
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="hm-video-wrap dm-intro-video">
      <iframe
        title="Introduction video"
        src={resolved.embedUrl}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
};

const DoctorManagementView = ({
  doctorRecord = null,
  degrees = [],
  mediaGallery = [],
  awardsRecognition = [],
  newsArticles = [],
  digitalCme = [],
  onEdit,
  showCreateProfile,
  onCreateProfile,
  useDemoFallback = false,
}) => {
  const isLive = Boolean(doctorRecord);
  const mapped = isLive ? mapDoctorApiToView(doctorRecord, { degrees }) : null;

  if (!isLive && !useDemoFallback) {
    return (
      <div className="dm-page">
        <p className="dm-text dm-text--muted">
          No doctor profile loaded. Use Create/ Edit Profile to complete your details.
        </p>
        {showCreateProfile && (
          <Button type="button" className="dm-edit-btn mt-3" onClick={onCreateProfile}>
            <MdAdd /> Create/ Edit Profile
          </Button>
        )}
      </div>
    );
  }

  const doctor = mapped
    ? {
        ...mapped,
        connect: buildConnectLinks(mapped.social),
      }
    : DM_DEMO_DOCTOR;

  const initials = doctor.name.replace(/Dr\.\s*/i, "").charAt(0) || "D";
  const profilePicUrl = doctor.profilePicPath
    ? `${UPLOADS_BASE}/uploads/${doctor.profilePicPath}`
    : null;
  const pdfUrl = doctor.profilePdfPath
    ? `${UPLOADS_BASE}/uploads/${doctor.profilePdfPath}`
    : null;

  const galleryImages = mediaGallery.filter((m) => m.media_type === "Image");
  const galleryVideos = mediaGallery.filter((m) => m.media_type === "Video");

  const publicationsRaw = doctorRecord?.articles_published ?? doctor.publications;
  const publicationsIsCount = publicationsRaw != null
    && publicationsRaw !== ""
    && /^\d+$/.test(String(publicationsRaw).trim());

  const doctorStats = [
    {
      id: "experience",
      label: "Experience",
      value: doctor.experience !== "—" ? doctor.experience : null,
      icon: MdWorkOutline,
    },
    {
      id: "surgeries",
      label: "Surgeries",
      value: doctor.surgeriesCount != null && doctor.surgeriesCount !== ""
        ? String(doctor.surgeriesCount)
        : null,
      icon: MdMedicalServices,
    },
    {
      id: "publications",
      label: "Publications",
      value: publicationsIsCount ? String(publicationsRaw).trim() : null,
      icon: MdMenuBook,
    },
  ].filter((s) => s.value);

  const renderMediaSections = () => (
    <div className="dm-media-stack">
      <DmCard title="Picture Gallery" icon={MdCollections}>
        {galleryImages.length > 0 ? (
          <div className="dm-hm-gallery">
            {galleryImages.map((item) => (
              <div key={item.media_id} className="dm-hm-gallery__item">
                <img
                  src={`${UPLOADS_BASE}/uploads/${item.media_upload}`}
                  alt={item.short_note || "Gallery image"}
                />
              </div>
            ))}
          </div>
        ) : (
          <p className="dm-text dm-text--muted dm-empty-hint">
            No pictures uploaded yet. Add images from Picture/ Video Gallery.
          </p>
        )}
      </DmCard>

      <DmCard title="Video Gallery" icon={MdVideocam}>
        {galleryVideos.length > 0 ? (
          <div className="dm-hm-gallery dm-hm-gallery--videos">
            {galleryVideos.map((item) => {
              const thumb = getYoutubeThumbnailUrl(item.media_link);
              const watchUrl = getYoutubeWatchUrl(item.media_link);
              return (
                <div key={item.media_id} className="dm-hm-gallery__item dm-hm-gallery__item--video">
                  {thumb ? (
                    <a href={watchUrl || item.media_link} target="_blank" rel="noopener noreferrer">
                      <img src={thumb} alt={item.short_note || "Video"} />
                      <span className="dm-hm-gallery__play"><MdPlayArrow size={28} /></span>
                    </a>
                  ) : (
                    <a href={item.media_link} target="_blank" rel="noopener noreferrer" className="dm-hm-gallery__fallback">
                      <MdPlayArrow size={28} />
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="dm-text dm-text--muted dm-empty-hint">
            No videos added yet. Add video links from Picture/ Video Gallery.
          </p>
        )}
      </DmCard>

      <DmCard title="Awards & Recognition" icon={MdWorkspacePremium}>
        {awardsRecognition.length > 0 ? (
          <div className="hm-awards-scroll">
            <div className="hm-awards-scroll__track">
              {awardsRecognition.map((award) => (
                <div key={award.award_id} className="hm-awards-scroll__item">
                  <AwardCard {...mapAwardToCardProps(award)} />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="dm-text dm-text--muted dm-empty-hint">
            No awards added yet. Add awards from Awards &amp; Recognitions menu.
          </p>
        )}
      </DmCard>

      <DmCard title="News & Articles" icon={MdArticle}>
        {newsArticles.length > 0 ? (
          <div className="hm-articles-scroll">
            <div className="hm-articles-scroll__track">
              {newsArticles.map((article) => (
                <div key={article.news_article_id} className="hm-articles-scroll__item">
                  <ArticleCard {...mapArticleToCardProps(article)} />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="dm-text dm-text--muted dm-empty-hint">
            No news articles added yet. Add articles from News &amp; Articles menu.
          </p>
        )}
      </DmCard>

      <DmCard title="Digital CME" icon={MdSchool}>
        {digitalCme.length > 0 ? (
          <div className="hm-cme-scroll">
            <div className="hm-cme-scroll__track">
              {digitalCme.map((item) => (
                <div key={item.digital_cme_id} className="hm-cme-scroll__item">
                  <VideoCard {...mapCmeToVideoCardProps(item)} />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="dm-text dm-text--muted dm-empty-hint">
            No digital CME added yet. Add sessions from Digital CME menu.
          </p>
        )}
      </DmCard>
    </div>
  );

  return (
    <div className="dm-page">
      <div className="dm-topbar">
        <div className="dm-brand">
          <MdMedicalServices className="dm-brand-icon" />
          <span>Doctor Profile</span>
        </div>
        <div className="dm-topbar-actions">
          {onEdit && (
            <Button type="button" className="dm-edit-btn" onClick={onEdit}>
              <MdEdit /> Edit
            </Button>
          )}
          {showCreateProfile && (
            <Button type="button" className="dm-edit-btn" onClick={onCreateProfile}>
              <MdAdd /> Create/ Edit Profile
            </Button>
          )}
        </div>
      </div>

      <div className="dm-hero">
        <div className="dm-avatar">
          {profilePicUrl ? (
            <img src={profilePicUrl} alt={doctor.name} />
          ) : (
            <span>{initials}</span>
          )}
        </div>
        <div className="dm-hero-content">
          {doctor.prefix && <span className="dm-hero-prefix">{doctor.prefix}</span>}
          <h1 className="dm-hero-name">{doctor.name}</h1>
          {doctor.specialty && <p className="dm-hero-specialty">{doctor.specialty}</p>}
          {doctor.subtitle && <p className="dm-hero-sub">{doctor.subtitle}</p>}
          <div className="dm-badges">
            <span className="dm-badge">
              <MdWorkOutline size={14} /> {doctor.experience}
            </span>
            {doctor.surgeriesCount != null && doctor.surgeriesCount !== "" && (
              <span className="dm-badge">
                <MdCalendarToday size={14} /> {doctor.surgeriesCount} surgeries
              </span>
            )}
            {doctor.consultFee !== "—" && (
              <span className="dm-badge">
                <MdCalendarToday size={14} /> Consult {doctor.consultFee}
              </span>
            )}
            {doctor.language !== "—" && (
              <span className="dm-badge">
                <MdTranslate size={14} /> {doctor.language}
              </span>
            )}
          </div>
        </div>
      </div>

      {doctorStats.length > 0 && (
        <div className="dm-stats-row">
          {doctorStats.map(({ id, label, value, icon: Icon }) => (
            <div key={id} className="dm-stat-card">
              <div className="dm-stat-icon"><Icon /></div>
              <div className="dm-stat-text">
                <span className="dm-stat-label">{label}</span>
                <span className="dm-stat-value">{value}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="dm-content-grid">
        <div className="dm-main">
          <DmCard title="About" icon={MdMedicalServices}>
            <p className="dm-text">
              {doctor.about || "No profile description added yet."}
            </p>
          </DmCard>

          <DmCard title="Qualifications" icon={MdSchool}>
            {doctor.qualifications?.length > 0 ? (
              <div className="dm-tags">
                {doctor.qualifications.map((q) => (
                  <span key={q} className="dm-tag">{q}</span>
                ))}
              </div>
            ) : (
              <p className="dm-text dm-text--muted">No qualifications added yet.</p>
            )}
          </DmCard>

          <DmCard title="Introduction" icon={MdVideoLibrary}>
            {doctor.introductionVideo ? (
              <DmIntroductionVideo videoUrl={doctor.introductionVideo} />
            ) : (
              <p className="dm-text dm-text--muted">No introduction video added yet.</p>
            )}
          </DmCard>

          {(publicationsRaw != null && publicationsRaw !== "" && !publicationsIsCount) && (
            <DmCard title="Publications" icon={MdMenuBook}>
              <div className="dm-quote">
                <span className="dm-quote-bar" />
                <p>{publicationsRaw}</p>
              </div>
            </DmCard>
          )}

          {renderMediaSections()}
        </div>

        <aside className="dm-sidebar">
          <DmCard title="Fees" icon={MdPayments}>
            <DmInfoRow label="Consultation" value={doctor.fees.consultation} />
            <DmInfoRow label="Follow-up" value={doctor.fees.followUp} />
          </DmCard>

          <DmCard title="Registration" icon={MdBadge}>
            <DmInfoRow label="Council" value={doctor.registration.council} />
            <DmInfoRow label="Number" value={doctor.registration.number} />
          </DmCard>

          <DmCard title="Contact" icon={MdPhone}>
            <div className="dm-contact-list">
              <div className="dm-contact-item">
                <MdPhone />
                <span>{doctor.contact.phone}</span>
              </div>
              <div className="dm-contact-item">
                <MdEmail />
                <span>{doctor.contact.email}</span>
              </div>
              <div className="dm-contact-item">
                <MdLocationOn />
                <span>{doctor.contact.address}</span>
              </div>
            </div>
          </DmCard>

          {pdfUrl ? (
            <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="dm-cv-btn">
              <MdPictureAsPdf />
              <span>Download CV</span>
              <MdDownload />
            </a>
          ) : (
            <button type="button" className="dm-cv-btn" disabled>
              <MdPictureAsPdf />
              <span>Download CV</span>
              <MdDownload />
            </button>
          )}

          <DmCard title="Connect">
            {doctor.connect?.length > 0 ? (
              <div className="dm-connect-grid dm-connect-grid--compact">
                {doctor.connect.map(({ key, label, icon: Icon, url }) => (
                  <a key={key} href={url} className="dm-connect-btn" target="_blank" rel="noopener noreferrer">
                    <Icon />
                    <span>{label}</span>
                    <MdOpenInNew className="dm-connect-ext" />
                  </a>
                ))}
              </div>
            ) : (
              <p className="dm-text dm-text--muted">No social links added yet.</p>
            )}
          </DmCard>
        </aside>
      </div>
    </div>
  );
};

export default DoctorManagementView;
